'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { apiGetPopularDeals } from '@/apis/page_optimization'
import { getBaseImageUrl, getCurrencySymbol, getMerchantHref, getProductDetailHref, discardHTMLTags, calculateOfferDuration, extractPromoDiscountTag } from '@/constants/hooks'
import { Tag, Clock, ShoppingBag, Star, ChevronRight, Zap, Truck, Percent, ArrowRight, Eye } from 'lucide-react'
import Theme18OfferLink from './Theme18OfferLink'

const ProductDetailsModal = dynamic(() => import('./ProductDetailsModal'), { ssr: false })

const C = { PRIMARY: '#F8F246', SECONDARY: '#8C5826', YELLOW: '#FF9934', BROWN: '#8C5826', LIGHT: '#fffbf0' }

const getExpiryTag = (endDate: string) => {
    const duration = calculateOfferDuration(endDate)
    if (duration === 'Offer expired') return { text: 'Expired', color: '#ef4444', bg: '#fee2e2' }
    if (duration === 'Last day') return { text: 'Last Day', color: '#f59e0b', bg: '#fef3c7' }
    return { text: duration, color: '#2CBBC3', bg: '#ccfbf1' }
}

const getDiscountTag = (title: string) => {
    const tag = extractPromoDiscountTag(title)
    if (!tag) return null
    if (tag.includes('%')) return { text: tag, color: '#FF9934', bg: '#fef3c7' }
    if (tag === 'BOGO') return { text: 'BOGO', color: '#2CBBC3', bg: '#ccfbf1' }
    if (tag === 'Free Shipping' || tag === 'Free Delivery') return { text: 'Free Shipping', color: '#10b981', bg: '#d1fae5' }
    if (tag === 'Free Gift') return { text: 'Free Gift', color: '#8b5cf6', bg: '#ede9fe' }
    return { text: tag, color: '#FF9934', bg: '#fef3c7' }
}

interface Props { companyId: string; slug_type: string; mer_slug: string }

const PetCareFeaturedDeals = ({ companyId, slug_type, mer_slug }: Props) => {
    const [offers, setOffers] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedOffer, setSelectedOffer] = useState<any>(null)
    const currency = getCurrencySymbol()

    useEffect(() => {
        apiGetPopularDeals(companyId)
            .then(r => r?.data?.offers && setOffers(r.data.offers.slice(0, 6)))
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [companyId])

    if (loading) return (
        <div className="max-w-7xl mx-auto px-4 py-10">
            <div className="animate-pulse">
                <div className="h-8 bg-gray-200 rounded w-48 mb-8"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => <div key={i} className="h-80 bg-gray-200 rounded-3xl"></div>)}
                </div>
            </div>
        </div>
    )

    return (
        <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10">
                <div>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-2"
                        style={{ background: 'rgba(140, 88, 38, 0.08)', color: '#8C5826' }}>
                        Featured Deals
                    </div>
                    <h2 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight">
                        Today&apos;s <span className="px-2 py-0.5 rounded-xl" style={{ color: '#8C5826', background: '#F8F24640' }}>Best Deals</span>
                    </h2>
                    <p className="text-gray-500 mt-1.5 text-sm">Exclusive discounts on premium pet care products</p>
                </div>
                <Link href="/all-products"
                    className="group inline-flex items-center gap-2 mt-4 md:mt-0 px-5 py-2.5 rounded-2xl font-extrabold text-xs sm:text-sm transition-all duration-300 hover:scale-105 shadow-sm whitespace-nowrap flex-shrink-0"
                    style={{
                        background: '#F8F246',
                        color: '#8C5826',
                        border: '1px solid rgba(140,88,38,0.15)',
                        boxShadow: '0 4px 16px rgba(248, 242, 70, 0.35)',
                    }}>
                    <span>View All Deals</span>
                    <ChevronRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {offers.map((offerItem, index) => {
                    const offer = offerItem.offer
                    const merchant = offerItem.merchant
                    const domain = typeof window !== 'undefined' ? window.location.hostname : ''
                    const imageUrl = offer.product_image && domain ? getBaseImageUrl(domain, offer.product_image, '/shared-assets/placeholder-img/placeholder.png') : merchant?.merchant_logo && domain ? getBaseImageUrl(domain, merchant.merchant_logo, '/shared-assets/placeholder-img/placeholder.png') : '/shared-assets/placeholder-img/placeholder.png'
                    const expiryTag = offer.end_date ? getExpiryTag(offer.end_date) : null
                    const discountTag = getDiscountTag(offer.offer_title || '')
                    const merchantHref = getMerchantHref(merchant, mer_slug, slug_type)
                    const detailUrl = offer?.slug && offer?.is_detail === 1 ? getProductDetailHref(merchant, slug_type, offer.slug) : null

                    const originalPrice = offer?.original_price ? parseFloat(offer.original_price) : 0
                    const salePrice = offer?.sale_price ? parseFloat(offer.sale_price) : 0
                    const offerType = offer?.offer_type?.name || 'deal'
                    const isCoupon = !!offer?.coupon_code

                    return (
                        <div key={offerItem.id || index} className="group relative bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-300 flex flex-col h-full overflow-hidden hover:-translate-y-1.5">
                            
                            {/* Image Header & Floating Badges */}
                            <div className="relative h-52 w-full bg-gradient-to-br from-amber-50/40 via-yellow-50/10 to-transparent overflow-hidden flex-shrink-0 flex items-center justify-center p-6">
                                <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 80%, #F8F24680 0%, transparent 60%)' }} />
                                
                                {/* Top Badges */}
                                <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-1.5">
                                    {discountTag && (
                                        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full font-extrabold text-xs shadow-sm backdrop-blur-md"
                                            style={{ background: discountTag.bg, color: discountTag.color }}>
                                            <Percent size={10} />
                                            {discountTag.text}
                                        </div>
                                    )}
                                </div>
                                <div className="absolute top-3 right-3 z-10">
                                    {expiryTag && (
                                        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold shadow-sm backdrop-blur-md"
                                            style={{ background: expiryTag.bg, color: expiryTag.color }}>
                                            <Clock size={10} />
                                            {expiryTag.text}
                                        </div>
                                    )}
                                </div>

                                {/* Product Image */}
                                {imageUrl && (
                                    <div className="absolute inset-0 flex items-center justify-center p-6">
                                        <Image src={imageUrl} alt={offer.offer_title || 'Deal Image'} fill
                                            className="object-contain p-6 group-hover:scale-105 transition-transform duration-500"
                                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" />
                                    </div>
                                )}
                            </div>

                            {/* Card Content Section */}
                            <div className="p-5 flex flex-col flex-1 justify-between bg-white">
                                <div className="space-y-2">
                                    {/* Merchant Link */}
                                    <Link href={merchantHref} className="text-xs font-bold tracking-wider uppercase hover:underline block text-center"
                                        style={{ color: '#8C5826' }}>
                                        {merchant?.merchant_name || 'Pet Store'}
                                    </Link>

                                    {/* Offer Title */}
                                    {detailUrl ? (
                                        <Link href={detailUrl} className="block">
                                            <h3 className="text-base font-bold text-gray-900 text-center line-clamp-2 hover:text-amber-800 transition-colors leading-snug">
                                                {offer.offer_title ? discardHTMLTags(offer.offer_title) : 'Exclusive Pet Care Deal'}
                                            </h3>
                                        </Link>
                                    ) : (
                                        <Theme18OfferLink
                                            unique_id={offer.unique_id}
                                            outUrl={offer.url || merchantHref}
                                            merchantHref={merchantHref}
                                            domain={domain}
                                            couponCode={offer.coupon_code || undefined}
                                            customClass="block"
                                        >
                                            <h3 className="text-base font-bold text-gray-900 text-center line-clamp-2 hover:text-amber-800 transition-colors leading-snug">
                                                {offer.offer_title ? discardHTMLTags(offer.offer_title) : 'Exclusive Pet Care Deal'}
                                            </h3>
                                        </Theme18OfferLink>
                                    )}

                                    {/* Price Row (Centered) */}
                                    {(salePrice > 0 || originalPrice > 0) && (
                                        <div className="flex items-baseline justify-center gap-2 pt-1 text-center">
                                            {salePrice > 0 && (
                                                <span className="text-base font-black text-gray-900">
                                                    {currency}{salePrice.toFixed(2)}
                                                </span>
                                            )}
                                            {originalPrice > 0 && originalPrice > salePrice && (
                                                <span className="text-xs text-gray-400 line-through font-medium">
                                                    {currency}{originalPrice.toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-auto pt-4">
                                    {/* Rating & View Details Row (Theme 15 style) */}
                                    <div className="flex items-center justify-between mb-4 pt-3 border-t border-gray-100 text-xs">
                                        {offer.rating > 0 ? (
                                            <div className="flex items-center gap-1 bg-amber-50 px-2.5 py-1 rounded-lg">
                                                <Star size={12} className="fill-yellow-400 text-yellow-400" />
                                                <span className="font-bold text-amber-900">{offer.rating}</span>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 font-medium">Verified Offer</span>
                                        )}
                                        <button onClick={() => setSelectedOffer(offerItem)}
                                            className="inline-flex items-center gap-1 text-xs text-[#8C5826] hover:text-[#5e3814] cursor-pointer hover:underline font-bold transition-colors">
                                            <Eye size={12} />
                                            <span>View Details</span>
                                        </button>
                                    </div>

                                    {/* Full-width CTA Action */}
                                    <Theme18OfferLink
                                        unique_id={offer.unique_id}
                                        outUrl={offer.url || merchantHref}
                                        merchantHref={merchantHref}
                                        domain={domain}
                                        couponCode={offer.coupon_code || undefined}
                                        customClass="w-full block"
                                    >
                                        <div className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl font-bold text-sm transition-all duration-300 shadow-sm hover:shadow cursor-pointer"
                                            style={{ background: `linear-gradient(135deg, ${C.PRIMARY} 0%, #ffe066 100%)`, color: C.BROWN }}>
                                            <ShoppingBag size={14} />
                                            <span>{isCoupon ? 'Show Coupon' : (offerType === 'product' ? 'Buy Now' : 'Get Deal')}</span>
                                            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </Theme18OfferLink>
                                </div>
                            </div>

                        </div>
                    )
                })}
            </div>

            {offers.length === 0 && !loading && (
                <div className="text-center py-12">
                    <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 shadow-sm"
                        style={{ background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)' }}>
                        <ShoppingBag size={32} color="#9ca3af" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-700 mb-2">No featured deals available</h3>
                    <p className="text-gray-500">Check back soon for exclusive pet care discounts!</p>
                </div>
            )}

            {selectedOffer && (
                <ProductDetailsModal
                    product={selectedOffer.offer}
                    merchantName={selectedOffer.merchant?.merchant_name || 'Pet Store'}
                    merchantLogo={selectedOffer.merchant?.merchant_logo || ''}
                    domain={typeof window !== 'undefined' ? window.location.hostname : ''}
                    isOpen={true}
                    onClose={() => setSelectedOffer(null)}
                />
            )}
        </div>
    )
}

export default PetCareFeaturedDeals